import Button from "components/form/Button";
import { SuccessPaymentIcon } from "icons/payments/successPayment";
import React, { useEffect, useState } from "react";
import Drawer from "components/ui-component/SideDrawer";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import moment from "moment";
import {
  decode,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import { useNavigate, useParams } from "react-router-dom";
import { formatPaymentListDate } from "utils/DateFormat";
import { useUserData } from "store/slices/authSlice";
import { useMediaQuery } from "react-responsive";
import InputField from "components/form/InputField";
import { useTestAttrsFactory } from "utils/testid";

interface agreementProps {
  defaultEmails?: any;
  onSendEmail: (values: any) => void;
  amount?: any;
  invoiceId?: any;
  onDownloadPdf?: any;
  tempData?: any;
  scheduled?: any;
  orderData?: any;
  cardData?: any;
  scheduleId?: any;
}

const PaymentAgreementProcessSucces = ({
  defaultEmails,
  onSendEmail = async () => {},
  amount,
  invoiceId,
  onDownloadPdf,
  tempData,
  scheduleId,
}: agreementProps) => {
  let dateToCheck = moment(tempData[0].billing_date);
  let today = moment().startOf("day");
  const [email, setEmail] = useState<string>("");
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [id, setId] = useState<any>();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { clusterId: cluster_id, id: actionId } = useParams();
  const userData = useUserData();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });
  const testAttrs = useTestAttrsFactory();

  const handleSendEmail = async () => {
    setIsEmailLoading(true);
    if (!validateEmail(email)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    try {
      let values = {
        emails: email,
        plan_id: decode(actionId || "")?.split("_")?.[0],
      };
      await onSendEmail(values);
    } catch (error) {
      console.log("handleSendEmail error =====", error);
    } finally {
      setEmail("");
      setIsEmailLoading(false);
    }
  };

  return (
    <>
    
    
    <div
      className="w-full min-h-full flex flex-col lg:justify-center justify-start items-center lg:gap-10 gap-6 lg:px-5 pt-6 lg:pt-0 pb-6"
      {...testAttrs("payment-agreement-success", "Payment agreement completed")}
    >
      <SuccessPaymentIcon aria-hidden="true" />
      <p className="lg:text-2xl text-xl font-medium text-txtPrimary text-center">
        Payment agreement completed
      </p>
      <div className="text-center">
        {!dateToCheck.isSame(today, "day") ? (
          <p className="px-2 py-5 bg-bgThird lg:text-xl text-lg text-txtParagraph rounded-md">
            Your first payment of{" "}
            <span className="font-bold">{formatCurrency(amount || tempData[0].amount)}</span> is
            scheduled for{" "}
            <span className="font-bold">
              {formatPaymentListDate(
                tempData[0].billing_date,
                userData?.data?.accountData?.timezone,
                "long"
              )}
              .
            </span>
          </p>
        ) : (
          <p className="px-[45px] py-5 bg-bgThird lg:text-xl text-lg text-txtParagraph rounded-md">
            Your first scheduled payment of{" "}
            <span className="font-bold">{formatCurrency(amount)}</span> has been
            received.
          </p>
        )}
        <div className="lg:text-base text-sm text-txtParagraph mt-4">
          <span
            className="text-btnPrimaryActive hover:!text-btnPrimaryHover cursor-pointer"
            role="button"
            tabIndex={0}
            {...testAttrs(
              "view-payment-plan-details",
              "View payment plan details"
            )}
            onClick={() => {
              if (dateToCheck.isSame(today, "day") && invoiceId && parseInt(invoiceId) > 0) {
                setId(invoiceId);
              } else {
                setId(scheduleId);
              }
              setIsPreview(true);
            }}
          >
            Click here
          </span>{" "}
          to view your full payment plan details.
        </div>
      </div>
      <div className="flex flex-col gap-[30px] lg:w-[75%] items-center justify-center text-center mt-[5%]">
        <div className="lg:text-base text-sm text-txtParagraph">
          A copy of this payment agreement will automatically be sent to{" "}
          <span className="font-bold">{defaultEmails}.</span> To send a copy to
          an additional email, enter the address below.
        </div>
        <div className="w-full">
          {" "}
          <InputField
            type="email"
            name="email"
            label=""
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setIsErr(false);
              setErrMsg(undefined);
            }}
            className="!py-3"
            error={errMsg}
            isErr={isErr}
            {...testAttrs(
              "payment-agreement-success-email",
              "Additional email for payment agreement copy"
            )}
          />
        </div>
        <div className="flex justify-center items-center gap-6 flex-col w-full">
          <div className="grid grid-cols-2 gap-5 w-full mt-2">
            <Button
              text="Send Email"
              className="w-full"
              isLoading={isEmailLoading}
              isDisabled={email.length === 0}
              {...testAttrs("send-agreement-email", "Send agreement email")}
              onClick={handleSendEmail}
            />
            <Button
              text="Download"
              isLoading={isDownloadLoading}
              {...testAttrs("download-agreement", "Download agreement")}
              onClick={async () => {
                setIsDownloadLoading(true);
                await onDownloadPdf({ id: invoiceId });
                setTimeout(() => {
                  setIsDownloadLoading(false);
                }, 500);
              }}
            />
          </div>
          <p
            className=" lg:text-base text-sm text-btnPrimaryActive cursor-pointer hover:!text-btnPrimaryHover "
            role="button"
            tabIndex={0}
            {...testAttrs("back-to-dashboard", "Back to dashboard")}
            onClick={() => {
              let slug = decode(actionId || "")?.split("_");
              let clusterId = cluster_id
                ? cluster_id
                : encode(`${slug[1]}`, "ci_");
              navigate(`/${clusterId}/dashboard`);
            }}
          >
            Back to dashboard
          </p>
        </div>
      </div>
    </div>
      <Drawer
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setId("");
        }}
        overlayColor="#000"
        lockBackgroundScroll={true}
        enableOverlay={true}
      >
        <div className="contents" {...testAttrs("payment-plan-details-drawer")}>
          <SubscriptionsPreview
            id={id}
            setId={setId}
            show={isPreview}
            setShow={setIsPreview}
            setHandleDrawerSize={() => {}}
            isApiCall={true}
            isScheduled={dateToCheck.isSame(today, "day") && invoiceId && parseInt(invoiceId) > 0}
            clusterId={
              cluster_id ? undefined : decode(actionId || "")?.split("_")?.[1]
            }
            isActionCenter={true}
          />
        </div>
      </Drawer>
    </>
  );
};

export default PaymentAgreementProcessSucces;
